import * as React from 'react'
import map from 'lodash/map'
import filter from 'lodash/filter'
import {firstResponseApiError} from '@frontrow/console/src/entities/models/api-error'

import {
  type CsvFileErrorT,
  csvFileErrorsFromError
} from '@frontrow/console/src/entities/models/roster-sync'
import {SpinnerOverlay} from '@frontrow/console/src/components/spinner'

import {type DistrictT} from '@frontrow/console/src/models/district'
import ImportSisIdsPage from '@frontrow/console/src/components/pages/import-sis-ids/import-sis-ids'
import {parseSisIdMatchStudents} from '@frontrow/console/src/models/student-sis-ids'
import {type StudentWithSisMatchesT} from '@frontrow/console/src/models/student-sis-ids'

import {
  fetchMatchResults,
  patchMatchResults
} from '@frontrow/console/src/helpers/api-import-sis-ids'

export type ImportFileType = 'teachers-csv' | 'students-csv' | 'enrollments-csv' | 'courses-csv'

export type PatchStudentSisIdT = {
  tag: 'PatchStudentSisId'
  studentId: number
  studentSisId: string
}

type State = {
  teachersFileValue: File | undefined | null
  studentsFileValue: File | undefined | null
  coursesFileValue: File | undefined | null
  enrollmentsFileValue: File | undefined | null
  districtName: string
  districtId: number | undefined | null
  preFetchError: string | undefined | null
  // SWR-like state for import sis ids data
  importSisIdsData: Array<StudentWithSisMatchesT> | undefined | null
  isLoading: boolean
  csvMatchErrors: Array<CsvFileErrorT> | undefined | null
  isPatchSuccess: boolean
  patchError: string | undefined | null
}

type Message = {
  teachersFileValue?: File | null
  studentsFileValue?: File | null
  coursesFileValue?: File | null
  enrollmentsFileValue?: File | null
  districtName?: string
  districtId?: number | null
  preFetchError?: string | null
  // SWR-like state updates
  importSisIdsData?: Array<StudentWithSisMatchesT> | null
  isLoading?: boolean
  csvMatchErrors?: Array<CsvFileErrorT> | null
  isPatchSuccess?: boolean
  patchError?: string | null
}

type Dispatcher = (message: Message) => void

const initialState: State = {
  teachersFileValue: null,
  studentsFileValue: null,
  coursesFileValue: null,
  enrollmentsFileValue: null,
  districtName: '',
  districtId: null,
  preFetchError: null,
  // SWR-like initial state
  importSisIdsData: null,
  isLoading: false,
  csvMatchErrors: null,
  isPatchSuccess: false,
  patchError: null
}

function reducer(state: State, message: Message): State {
  return {...state, ...message}
}

function useStateManagement(): [State, Dispatcher] {
  return React.useReducer(reducer, initialState)
}

function getHandleImportFile(deps: {
  dispatch: Dispatcher
}): (e: React.ChangeEvent<HTMLInputElement>, fileType: ImportFileType) => void {
  const {dispatch} = deps
  return (e: React.ChangeEvent<HTMLInputElement>, fileType: ImportFileType) => {
    const fileUploaded = e.target.files![0]
    if (fileUploaded) {
      switch (fileType) {
        case 'teachers-csv':
          dispatch({teachersFileValue: fileUploaded})
          break
        case 'students-csv':
          dispatch({studentsFileValue: fileUploaded})
          break
        case 'courses-csv':
          dispatch({coursesFileValue: fileUploaded})
          break
        case 'enrollments-csv':
          dispatch({enrollmentsFileValue: fileUploaded})
          break
        default:
          dispatch({preFetchError: 'Unknown Import File Type'})
      }
    }
  }
}

function getOnPostDistrictFetch(deps: {
  dispatch: Dispatcher
}): (districts: Array<DistrictT>) => void {
  const {dispatch} = deps
  return (districts: Array<DistrictT>) => {
    if (districts.length === 1) {
      dispatch({
        districtName: districts[0].name,
        districtId: districts[0].id,
        preFetchError: null
      })
    } else {
      dispatch({
        preFetchError: 'Cannot find district / Multiple district matches found'
      })
    }
  }
}

function getOnMatchStudents(deps: {state: State; dispatch: Dispatcher}) {
  const {dispatch, state} = deps
  return async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const {
      teachersFileValue,
      studentsFileValue,
      coursesFileValue,
      enrollmentsFileValue,
      districtId
    } = state

    if (
      teachersFileValue !== null &&
      teachersFileValue !== undefined &&
      studentsFileValue !== null &&
      studentsFileValue !== undefined &&
      coursesFileValue !== null &&
      coursesFileValue !== undefined &&
      enrollmentsFileValue !== null &&
      enrollmentsFileValue !== undefined &&
      districtId !== null &&
      districtId !== undefined
    ) {
      const fetchDataParams = {
        teachersCsv: teachersFileValue,
        studentsCsv: studentsFileValue,
        coursesCsv: coursesFileValue,
        enrollmentsCsv: enrollmentsFileValue
      }

      try {
        dispatch({
          isLoading: true,
          csvMatchErrors: null,
          importSisIdsData: null,
          isPatchSuccess: false,
          patchError: null,
          preFetchError: null
        })

        const response = await fetchMatchResults(fetchDataParams, districtId)
        const importSisIdsData = parseSisIdMatchStudents(response)

        dispatch({
          isLoading: false,
          csvMatchErrors: null,
          importSisIdsData,
          isPatchSuccess: false,
          patchError: null
        })
      } catch (error: unknown) {
        dispatch({
          isLoading: false,
          csvMatchErrors: csvFileErrorsFromError(error),
          importSisIdsData: null,
          isPatchSuccess: false,
          patchError: null
        })
      }
    } else {
      dispatch({preFetchError: 'Missing Files or Missing District Id'})
    }
  }
}

function usePatchStudentsSisIds(dispatch: Dispatcher) {
  return React.useCallback(
    async (patchSisIdsPayload: Array<PatchStudentSisIdT>) => {
      try {
        dispatch({
          isLoading: true,
          csvMatchErrors: null,
          importSisIdsData: null,
          isPatchSuccess: false,
          patchError: null
        })

        await patchMatchResults(patchSisIdsPayload)

        dispatch({
          isLoading: false,
          csvMatchErrors: null,
          importSisIdsData: null,
          isPatchSuccess: true,
          patchError: null
        })
      } catch (err: unknown) {
        const apiError = firstResponseApiError(err, 'Unknown student patch error.')

        dispatch({
          isLoading: false,
          csvMatchErrors: null,
          importSisIdsData: null,
          isPatchSuccess: false,
          patchError: apiError.message
        })
      }
    },
    [dispatch]
  )
}

export function ImportSisIdsPageContainer(): React.ReactElement {
  const [state, dispatch] = useStateManagement()
  const {
    teachersFileValue,
    studentsFileValue,
    coursesFileValue,
    enrollmentsFileValue,
    districtName,
    preFetchError,
    importSisIdsData,
    isLoading,
    csvMatchErrors,
    isPatchSuccess,
    patchError
  } = state

  const handleImportFile = getHandleImportFile({dispatch})
  const onPostDistrictFetch = getOnPostDistrictFetch({dispatch})
  const onMatchStudents = getOnMatchStudents({state, dispatch})
  const patchStudentsSisIds = usePatchStudentsSisIds(dispatch)

  /* Display stored data if there is no new data from api call*/
  /* Students with no sisMatches will not be displayed, filtered here*/
  const selectedStudents =
    importSisIdsData !== null && importSisIdsData !== undefined
      ? map(
          filter(importSisIdsData, student => {
            return student.sisMatches.length > 0
          }),
          student => {
            return {
              studentWithSisMatches: student,
              districtStudent: student.sisMatches[0],
              selected: false
            }
          }
        )
      : null

  return isLoading ? (
    <SpinnerOverlay />
  ) : (
    <ImportSisIdsPage
      onImport={handleImportFile}
      onMatchStudents={onMatchStudents}
      onPostFetch={onPostDistrictFetch}
      teachersFileUploaded={teachersFileValue !== null && teachersFileValue !== undefined}
      studentsFileUploaded={studentsFileValue !== null && studentsFileValue !== undefined}
      coursesFileUploaded={coursesFileValue !== null && coursesFileValue !== undefined}
      enrollmentsFileUploaded={enrollmentsFileValue !== null && enrollmentsFileValue !== undefined}
      onPatchSisIds={patchStudentsSisIds}
      students={selectedStudents}
      isPatchSuccess={isPatchSuccess}
      csvMatchErrors={csvMatchErrors}
      preFetchError={preFetchError}
      districtName={districtName}
      patchError={patchError}
    />
  )
}
